import React, {Component} from "react"

import {DisablePointerEventsOnScroll} from "@irati/ui/src/DisablePointerEventsOnScroll"
import {injectIntl} from "@irati/utils/lang"
import Loader from "@irati/utils/services/apollo/Loading/Loader"
import scrollAtBottom from "@irati/utils/scroll/scrollAtBottom"
import ListQuery, {CompanyValuesListQueryData} from "@irati/companyvalues/apollo/query/CompanyValuesList"
import {CompanyValue} from "@irati/companyvalues/types"
import authentication from "@irati/auth"

import {CompanyValueArchive} from "../index"
import {CompanyValueRow, Empty, Header, LoadMore} from "./index"

interface Props {
  onClick(): void
  setupView: boolean
}

interface State {
  archiveCompanyValue?: CompanyValue
  hasRefetchingError: boolean
}

interface FetchMore {
  (): Promise<any>
}

class CompanyValuesList extends Component<Props, State> {
  state = {
    archiveCompanyValue: undefined,
    hasRefetchingError: false
  }

  onLoadMore: FetchMore | null = null

  componentDidMount() {
    window.addEventListener("online", this.handleNetworkStatusChange)
    window.addEventListener("scroll", this.handleScroll)
  }

  componentWillUnmount() {
    window.removeEventListener("online", this.handleNetworkStatusChange)
    window.removeEventListener("scroll", this.handleScroll)
  }

  handleScroll = () => {
    const atBottom = scrollAtBottom()

    const {hasRefetchingError} = this.state

    if (atBottom && !hasRefetchingError) {
      this.fetchMore()
    }
  }

  handleNetworkStatusChange = () => {
    const {hasRefetchingError} = this.state

    if (navigator.onLine && hasRefetchingError) {
      this.fetchMore()
    }
  }

  fetchMore = () => {
    if (this.onLoadMore) {
      this.setState({
        hasRefetchingError: false
      })
      this.onLoadMore().catch(() => {
        this.setState({
          hasRefetchingError: true
        })
      })
    }
  }

  hasReceivedData(data?: CompanyValuesListQueryData) {
    return data && "companyValues" in data && "results" in data.companyValues && data.companyValues.results.length > 0
  }

  closeArchiveModal = () => this.setState({archiveCompanyValue: undefined})

  archiveCompanyValue = (companyValue: CompanyValue) => {
    this.setState({archiveCompanyValue: companyValue})
  }

  render() {
    const {onClick, setupView} = this.props
    const {archiveCompanyValue, hasRefetchingError} = this.state
    const tenantId = authentication.userTenant().uuid

    const params = {
      limit: 10,
      page: 0,
      tenantId
    }
    const calculatedFetchPolicy = setupView ? "network-only" : "cache-first"
    return (
      <>
        <DisablePointerEventsOnScroll>
          <ListQuery fetchPolicy={calculatedFetchPolicy} variables={params}>
            {({data, onLoadMore, refetch, loading: isLoading, error, networkStatus}) => {
              const hasData = this.hasReceivedData(data)
              const hasError = error || hasRefetchingError
              const isFetchingMore = networkStatus === 3
              this.onLoadMore = !isLoading ? () => onLoadMore() : null

              return isLoading && !isFetchingMore ? (
                <Loader />
              ) : (
                <>
                  <Header onClick={() => onClick()} count={data.companyValues.total} />
                  {data &&
                    hasData &&
                    data.companyValues.results.map((companyValue, index) => (
                      <CompanyValueRow
                        companyValue={companyValue}
                        key={companyValue.uuid}
                        id={`companyValue${index}`}
                        editCompanyValue={() => onClick(companyValue)}
                        archiveCompanyValue={() => this.archiveCompanyValue(companyValue)}
                      />
                    ))}
                  {isLoading && <Loader />}
                  {!isLoading && hasError && <LoadMore onClick={error ? () => refetch() : this.fetchMore} />}
                  {!isLoading && !hasError && !hasData && <Empty />}
                </>
              )
            }}
          </ListQuery>
        </DisablePointerEventsOnScroll>
        {archiveCompanyValue && <CompanyValueArchive closeArchiveModal={this.closeArchiveModal} companyValue={archiveCompanyValue} />}
      </>
    )
  }
}

export default injectIntl(CompanyValuesList)
